import { Form, Formik } from 'formik'
import { FC, useMemo, useRef, useState } from 'react'
import Col from 'react-bootstrap/Col'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'

import AppointmentAPI from '@api/appointmentsAPI'
import { PatientVisitsAPI } from '@api/patientVisitsAPI'
import QueueNumbersAPI from '@api/queueNumbersAPI'

import { SortDirectionEnum } from '@models/apiBase'
import { ID } from '@models/base'
import { CartTypeEnum } from '@models/cart'
import { Option } from '@models/option'
import { QueueModel } from '@models/queue'
import { QueueNumberPriorityEnum, QueueNumberTypeEnum } from '@models/queueNumber'
import { QueueNumberStatusEnum } from '@models/queueProcess'

import { Button } from '@components/Button'
import { CheckboxSwitch } from '@components/Form/CheckboxSwitch/CheckboxSwitch'
import { ImageUploadField } from '@components/Form/ImageUpload'
import { InputField } from '@components/Form/Input'
import { TextareaField } from '@components/Form/Textarea'
import { FormControl } from '@components/FormControl'

import AppointmentTimelineCard from '@modules/appointment/components/AppointmentTimelineCard'
import PatientVisitTypeSelectField from '@modules/patient-visit/components/PatientVisitTypeSelectField'
import { PermissionsControl } from '@modules/permissions'
import { usePermissions } from '@modules/permissions/core/PermissionsProvider'
import QueuePriorityRadioGroupField from '@modules/queues/components/QueueForm/fields/QueuePriorityRadioGroupField'
import QueuesSelectField from '@modules/queues/components/QueueSelectField'

import { PermissionEnum } from '@/constants/permission'
import { QUERIES } from '@/constants/queries'
import { useAlert } from '@/hooks'
import dayjs from '@/libs/dayjs'
import AxiosUtil from '@/utils/axiosUtil'
import FormUtil, { FormDisabledFlags, FormShape } from '@/utils/formUtil'
import TypeUtil from '@/utils/typeUtil'

import QueueNumberCounterSelectField from '../QueueNumberFormModal/fields/QueueNumberCounterSelectField'
import QueueNumberInsurancePayorSelectField from '../QueueNumberFormModal/fields/QueueNumberInsurancePayorSelectField'
import QueueNumberInsuranceSelectField from '../QueueNumberFormModal/fields/QueueNumberInsuranceSelectField'
import QueueNumberPatientCreateButton from '../QueueNumberFormModal/fields/QueueNumberPatientCreateButton'
import QueueNumberPatientRelativeCreateButton from '../QueueNumberFormModal/fields/QueueNumberPatientRelativeCreateButton'
import QueueNumberPatientRelativeSelectField from '../QueueNumberFormModal/fields/QueueNumberPatientRelativeSelectField'
import QueueNumberTypeSelectField from '../QueueNumberFormModal/fields/QueueNumberTypeSelectField'
import QueuePatientSelectField from '../QueueNumberFormModal/fields/QueuePatientSelectField'
import QueueNumberCartTypeSelectField from './fields/QueueNumberCartTypeSelectField'

type QueueNumberFormShape = FormShape<{
  insurance_card_filepath: string
  insurance_id: Option
  insurance_payor_id: Option<{ call_center_number: string | null }>
  insurance_card_number: string
  is_queue_allow_multiple_process: boolean
  cart_type: Option
  name: string
  note: string
  patient_id: Option
  patient_relative_id: Option
  patient_visit_type: Option
  preferred_counter_id: Option
  priority: QueueNumberPriorityEnum
  queue_id: Option
  queue_number_type: Option
}>

type QueueNumberFormDisabledFlags = Partial<FormDisabledFlags<QueueNumberFormShape>>

type IsGenerateProps =
  | {
      isGenerate: true
      queueNumberId?: never
    }
  | {
      isGenerate: false
      queueNumberId: ID
    }

type Props = {
  isShow: boolean
  disabledFields?: QueueNumberFormDisabledFlags
  currentQueue?: Pick<QueueModel, 'name' | 'id' | 'allow_multiple_process'>
  initialValues?: QueueNumberFormShape
  onSubmit: (values: QueueNumberFormShape) => Promise<void>
  onClose: () => void
  onExited?: () => void
} & IsGenerateProps

const QueueNumberFormModal: FC<Props> = ({
  isShow,
  queueNumberId,
  currentQueue,
  isGenerate,
  disabledFields: disabledFieldsFlag,
  initialValues,
  onSubmit,
  onClose,
  onExited,
}) => {
  const intl = useIntl()
  const alert = useAlert()
  const { hasPermissions } = usePermissions()
  const [error, setError] = useState<string>()
  const [patientHasRelative, setPatientHasRelative] = useState<boolean>()
  const [patientId, setPatientId] = useState<ID>()
  const startOfToday = useRef(dayjs().startOf('day').toTzISOString()).current
  const endOfToday = useRef(dayjs().endOf('day').toTzISOString()).current
  const isEnableAppointment = TypeUtil.isDefined(patientId)

  const { data: queueNumber } = useQuery({
    enabled: isShow && !isGenerate && TypeUtil.isDefined(queueNumberId),
    queryKey: [QUERIES.QUEUE_NUMBER_DETAIL, queueNumberId],
    queryFn: () => QueueNumbersAPI.get(queueNumberId!).then((r) => r.data.queue_node),
    onSuccess: (r) => {
      if (r.patient_id) {
        setPatientId(r.patient_id)
      }
      setPatientHasRelative(TypeUtil.isDefined(r?.patient_relative_id))
    },
  })

  const { data: appointments } = useQuery({
    enabled: isEnableAppointment,
    queryKey: [QUERIES.APPOINTMENTS_LIST, { startOfToday, endOfToday, patientId }],
    queryFn: () =>
      AppointmentAPI.filterForGenerateQueueNumberPatient({
        started_at_start: startOfToday,
        started_at_end: endOfToday, // Get All Patient Future Appointment
        patient_id: patientId!,
        sorts: [
          {
            field: 'started_at',
            direction: SortDirectionEnum.ASC,
          },
        ],
      }),
    select: (response) => response.data?.nodes,
  })

  const insuranceImageLoader = useMemo(() => {
    if (
      queueNumber &&
      queueNumber.insurance &&
      hasPermissions([PermissionEnum.PATIENT_VISIT_INSURANCE_CARD_FILE_SHOW_FOR_QUEUE_NUMBER]) &&
      TypeUtil.isDefined(queueNumber.patient_visit_id)
    ) {
      return () => PatientVisitsAPI.getInsuranceCardFileForQueueNumber(queueNumber.patient_visit_id!)
    }

    return undefined
  }, [hasPermissions, queueNumber])

  const hasMedicalRecord = !!queueNumber?.has_medical_records

  const disabledFields: QueueNumberFormDisabledFlags = {
    insurance_card_filepath: hasMedicalRecord,
    insurance_card_number: hasMedicalRecord,
    insurance_id: hasMedicalRecord,
    insurance_payor_id: hasMedicalRecord,
    ...disabledFieldsFlag,
  }

  const defaultValues: QueueNumberFormShape = {
    cart_type: new Option({
      label: intl.formatMessage({ id: 'queue_number.form.radio.clinic' }),
      value: CartTypeEnum.MEDICAL,
    }),
    priority: QueueNumberPriorityEnum.Green,
    queue_number_type: new Option({
      label: intl.formatMessage({
        id: `enum.queue_number_type.${QueueNumberTypeEnum.Normal.toLowerCase()}`,
      }),
      value: QueueNumberTypeEnum.Normal,
    }),
  }

  const mergedValues: QueueNumberFormShape | undefined = queueNumber
    ? {
        cart_type:
          queueNumber.cart_type === CartTypeEnum.RETAIL
            ? new Option({
                label: intl.formatMessage({ id: 'queue_number.form.radio.pharmacy' }),
                value: CartTypeEnum.RETAIL,
              })
            : defaultValues.cart_type,
        queue_number_type:
          queueNumber.type === QueueNumberTypeEnum.Appointment
            ? new Option({
                label: intl.formatMessage({
                  id: `enum.queue_number_type.${QueueNumberTypeEnum.Appointment.toLowerCase()}`,
                }),
                value: QueueNumberTypeEnum.Appointment,
              })
            : defaultValues.queue_number_type,
        name: queueNumber.name,
        is_queue_allow_multiple_process: currentQueue ? currentQueue.allow_multiple_process : null,
        note: queueNumber.patient_visit_note,
        queue_id: currentQueue
          ? new Option({
              label: currentQueue.name,
              value: currentQueue.id,
            })
          : null,
        patient_id:
          queueNumber.patient_id && queueNumber.patient_code && queueNumber.patient_name
            ? new Option({
                label: `#${queueNumber.patient_code} - ${queueNumber.patient_name}`,
                value: queueNumber.patient_id,
              })
            : null,
        patient_visit_type: queueNumber?.patient_visit_type
          ? new Option({
              label: intl.formatMessage({
                id: `enum.patient_visit_type.${queueNumber.patient_visit_type.toLowerCase()}`,
              }),
              value: queueNumber.patient_visit_type,
            })
          : null,
        preferred_counter_id:
          queueNumber.queue_counter_id && queueNumber.queue_counter_name
            ? new Option({
                value: queueNumber.queue_counter_id,
                label: queueNumber.queue_counter_name,
              })
            : null,
        patient_relative_id:
          queueNumber.patient_relative_id && queueNumber.patient_relative_name
            ? new Option({
                value: queueNumber.patient_relative_id,
                label: queueNumber.patient_relative_name,
              })
            : null,
        insurance_card_number: queueNumber.insurance_card_number,
        insurance_id: queueNumber.insurance
          ? new Option({
              value: queueNumber.insurance.id,
              label: queueNumber.insurance.name,
            })
          : null,
        insurance_payor_id: queueNumber.insurance_payor
          ? new Option({
              value: queueNumber.insurance_payor.id,
              label: queueNumber.insurance_payor.name,
              data: {
                call_center_number: queueNumber.insurance_payor.call_center_number,
              },
            })
          : null,
        ...initialValues,
      }
    : initialValues

  const handleClose = () => {
    setError(undefined)
    setPatientHasRelative(false)
    onClose()
  }

  if (queueNumberId && !queueNumber) {
    return null
  }

  return (
    <Modal
      centered
      show={isShow}
      size={isEnableAppointment ? 'xl' : 'lg'}
      onHide={handleClose}
      onExited={onExited}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h3">
          {intl.formatMessage({ id: isGenerate ? 'queue_modal.generate.title' : 'queue_modal.edit.title' })}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="mx-5 mx-xl-15 my-7">
        <Row>
          <Col xs={isEnableAppointment ? 6 : 12}>
            <Formik<QueueNumberFormShape>
              enableReinitialize
              initialValues={{
                ...defaultValues,
                ...mergedValues,
              }}
              onSubmit={async (rawValues, { setSubmitting, setErrors }) => {
                try {
                  setError(undefined)
                  setSubmitting(true)

                  const values = { ...rawValues }

                  if (hasMedicalRecord) {
                    delete values.insurance_card_filepath
                    delete values.insurance_card_number
                    delete values.insurance_id
                    delete values.insurance_payor_id
                  }

                  await onSubmit(values)
                } catch (err) {
                  if (AxiosUtil.isAxiosError(err)) {
                    const errors = FormUtil.parseErrorResponse(err.response?.data.errors)
                    if (errors) {
                      setErrors(errors)
                    } else {
                      setError(err.response?.data.message)
                    }
                  } else {
                    alert.error({ text: String(err) })
                  }
                } finally {
                  setSubmitting(false)
                }
              }}
            >
              {({ handleSubmit, errors, isSubmitting, values, setFieldValue }) => (
                <Form
                  className="form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  {error && <div className="alert alert-danger d-flex align-items-center p-5 mb-10">{error}</div>}

                  <FormControl>
                    <FormControl.Label>
                      <FormattedMessage id="queue_number.form.queue_number_department" />
                    </FormControl.Label>

                    <QueueNumberCartTypeSelectField
                      name="cart_type"
                      isDisabled={disabledFields.cart_type}
                    />
                    <FormControl.ErrorMessage message={errors.cart_type} />
                  </FormControl>

                  <FormControl>
                    <FormControl.Label>
                      <FormattedMessage id="queue_number.form.type" />
                    </FormControl.Label>

                    <QueueNumberTypeSelectField
                      name="queue_number_type"
                      isDisabled={disabledFields?.queue_number_type}
                    />
                    <FormControl.ErrorMessage message={errors.queue_number_type} />
                  </FormControl>

                  <Row>
                    <Col xs={6}>
                      <FormControl>
                        <FormControl.Label isRequired>
                          <FormattedMessage id="queue" />
                        </FormControl.Label>

                        <QueuesSelectField
                          name="queue_id"
                          onChange={(value) => {
                            setFieldValue('is_queue_allow_multiple_process', value?.data?.allow_multiple_process)
                            setFieldValue('preferred_counter_id', undefined)
                          }}
                          isDisabled={disabledFields?.queue_id}
                        />
                        <FormControl.ErrorMessage message={errors.queue_id} />
                      </FormControl>
                    </Col>

                    {!values.is_queue_allow_multiple_process && (
                      <Col xs={6}>
                        <PermissionsControl
                          allow={PermissionEnum.QUEUE_COUNTER_OPTION_FOR_QUEUE_NUMBER_GENERATE_FORM}
                          renderError
                        >
                          <FormControl>
                            <FormControl.Label>
                              <FormattedMessage id="choose.preferred.counter" />
                            </FormControl.Label>
                            <QueueNumberCounterSelectField
                              isClearable
                              queueId={values.queue_id?.value as ID}
                              name="preferred_counter_id"
                              isDisabled={
                                disabledFields?.preferred_counter_id ||
                                values.queue_id === undefined ||
                                (queueNumber &&
                                  queueNumber.status !== QueueNumberStatusEnum.Skipped &&
                                  queueNumber.status !== QueueNumberStatusEnum.Waiting)
                              }
                            />
                            <FormControl.ErrorMessage message={errors.preferred_counter_id} />
                          </FormControl>
                        </PermissionsControl>
                      </Col>
                    )}
                  </Row>

                  <FormControl>
                    <FormControl.Label>
                      <FormattedMessage id="vocabulary.patient" />
                    </FormControl.Label>
                    <div className="d-flex gap-5">
                      <div className="flex-equal">
                        <PermissionsControl
                          allow={PermissionEnum.PATIENT_OPTION_FOR_QUEUE_NUMBER_GENERATE_FORM}
                          renderError
                        >
                          <QueuePatientSelectField
                            name="patient_id"
                            isDisabled={disabledFields?.patient_id}
                            onChange={(option) => {
                              setPatientId(option ? (option.value as ID) : undefined)
                              setFieldValue('name', null)
                              if (values.patient_id !== option?.value) {
                                setFieldValue('patient_relative_id', null)
                              }
                            }}
                          />
                        </PermissionsControl>
                      </div>

                      {values.patient_id && (
                        <PermissionsControl allow={PermissionEnum.PATIENT_SHOW}>
                          <Link
                            className="btn btn-icon btn-secondary"
                            to={`/patients/overview/${values.patient_id?.value}`}
                            onClick={handleClose}
                          >
                            <i className="fa-solid fa-eye" />
                          </Link>
                        </PermissionsControl>
                      )}

                      <QueueNumberPatientCreateButton
                        onCreated={(patient) => {
                          setPatientId(patient.id)
                          setFieldValue(
                            'patient_id',
                            new Option({
                              label: `${patient.patient_id} - ${patient.full_name}`,
                              value: patient.id,
                              data: patient,
                            })
                          )
                        }}
                      />
                    </div>
                    <FormControl.ErrorMessage message={errors.patient_id} />
                  </FormControl>

                  {!values.patient_id && (
                    <FormControl>
                      <FormControl.Label isRequired>
                        <FormattedMessage id="name" />
                      </FormControl.Label>
                      <InputField
                        name="name"
                        isDisabled={disabledFields?.name}
                      />
                      <FormControl.ErrorMessage message={errors.name} />
                    </FormControl>
                  )}

                  {values.patient_id && (
                    <>
                      <FormControl>
                        <CheckboxSwitch
                          name="is_have_relative"
                          value={patientHasRelative}
                          label={intl.formatMessage({ id: 'queue_number.form.with_relative' })}
                          onChange={(value) => {
                            setPatientHasRelative(value)
                            setFieldValue('patient_relative_id', undefined)
                          }}
                        />
                      </FormControl>

                      {patientHasRelative && (
                        <FormControl>
                          <FormControl.Label>
                            <FormattedMessage id="queue_number.form.patient_relative" />
                          </FormControl.Label>
                          <div className="d-flex gap-5">
                            <div className="flex-equal">
                              <PermissionsControl
                                allow={PermissionEnum.PATIENT_RELATIVE_OPTION_FOR_QUEUE_NUMBER_FORM}
                                renderError
                              >
                                <QueueNumberPatientRelativeSelectField
                                  isClearable
                                  name="patient_relative_id"
                                  patientId={values.patient_id.value as ID}
                                  isDisabled={
                                    disabledFields?.patient_relative_id
                                  }
                                  defaultValue={
                                    mergedValues?.patient_relative_id ? mergedValues.patient_relative_id : undefined
                                  }
                                />
                              </PermissionsControl>
                            </div>

                            <QueueNumberPatientRelativeCreateButton
                              patientId={values.patient_id.value!}
                              onCreated={(patientRelative) => {
                                setFieldValue(
                                  'patient_relative_id',
                                  Option.fromObject(patientRelative, 'id', 'full_name')
                                )
                              }}
                            />
                          </div>
                          <FormControl.ErrorMessage message={errors.patient_relative_id} />
                        </FormControl>
                      )}

                      <FormControl>
                        <FormControl.Label isRequired>
                          <FormattedMessage id="queue_number.form.visit_type" />
                        </FormControl.Label>

                        <PatientVisitTypeSelectField
                          name="patient_visit_type"
                          isDisabled={disabledFields?.patient_visit_type}
                        />
                        <FormControl.ErrorMessage message={errors.patient_visit_type} />
                      </FormControl>

                      <PermissionsControl allow={[PermissionEnum.INSURANCE_OPTION_FOR_QUEUE_NUMBER]}>
                        <FormControl>
                          <FormControl.Label>
                            <FormattedMessage id="queue_number.form.insurance" />
                          </FormControl.Label>
                          <QueueNumberInsuranceSelectField
                            name="insurance_id"
                            isDisabled={disabledFields?.insurance_id}
                            isClearable={true}
                            onChange={(nextValue) => {
                              if (!nextValue) {
                                setFieldValue('insurance_payor_id', null)
                                setFieldValue('insurance_card_number', null)
                                setFieldValue('insurance_card_filepath', null)
                              } else {
                                setFieldValue('insurance_payor_id', null)
                              }
                            }}
                          />
                          <FormControl.ErrorMessage message={errors.insurance_id} />
                        </FormControl>

                        {TypeUtil.isDefined(values.insurance_id) && (
                          <>
                            <PermissionsControl
                              allow={[PermissionEnum.INSURANCE_PAYOR_OPTION_FOR_QUEUE_NUMBER]}
                              renderError
                            >
                              <FormControl>
                                <FormControl.Label isRequired>
                                  <FormattedMessage id="queue_number.form.insurance_payor" />
                                </FormControl.Label>

                                <QueueNumberInsurancePayorSelectField
                                  name="insurance_payor_id"
                                  isDisabled={disabledFields?.insurance_payor_id}
                                  insuranceId={values.insurance_id.value ?? null}
                                />
                                <FormControl.ErrorMessage message={errors.insurance_payor_id} />
                              </FormControl>

                              <FormControl>
                                <FormControl.Label>
                                  <FormattedMessage id="insurance_payor.model.call_center_number" />
                                </FormControl.Label>
                                <span className="fw-medium">
                                  {values.insurance_payor_id?.data?.call_center_number || '-'}
                                </span>
                              </FormControl>
                            </PermissionsControl>

                            <FormControl>
                              <FormControl.Label isRequired>
                                <FormattedMessage id="queue_number.form.insurance_card_number" />
                              </FormControl.Label>
                              <InputField
                                name="insurance_card_number"
                                isDisabled={disabledFields?.insurance_card_number}
                              />
                              <FormControl.ErrorMessage message={errors.insurance_card_number} />
                            </FormControl>

                            <PermissionsControl
                              allow={[PermissionEnum.QUEUE_NUMBER_UPLOAD_INSURANCE_CARD]}
                              renderError
                            >
                              <FormControl>
                                <FormControl.Label isRequired>
                                  <FormattedMessage id="queue_number.form.insurance_card_photo" />
                                </FormControl.Label>
                                <ImageUploadField
                                  name="insurance_card_filepath"
                                  initialImageSrc={insuranceImageLoader}
                                  uploadFn={QueueNumbersAPI.uploadInsuranceCard}
                                  accessor={(response) => response.data?.path}
                                  accept={['image/jpg', 'image/jpeg', 'image/jfif', 'image/png']}
                                  aspectRatio={3 / 2}
                                  width={400}
                                  isDisabled={disabledFields?.insurance_card_filepath}
                                />
                                <FormControl.ErrorMessage message={errors.insurance_card_filepath} />
                              </FormControl>
                            </PermissionsControl>
                          </>
                        )}
                      </PermissionsControl>

                      <FormControl>
                        <FormControl.Label>
                          <FormattedMessage id="queue_number.form.triage_note" />
                        </FormControl.Label>
                        <TextareaField
                          name="note"
                          rows={5}
                          disabled={disabledFields?.note}
                        />
                        <FormControl.ErrorMessage message={errors.note} />
                      </FormControl>
                    </>
                  )}

                  <FormControl>
                    <FormControl.Label>
                      <FormattedMessage id="queue_number.form.priority" />
                    </FormControl.Label>
                    <QueuePriorityRadioGroupField
                      name="priority"
                      disabled={disabledFields?.priority}
                    />
                    <FormControl.ErrorMessage message={errors.priority} />
                  </FormControl>

                  <div className="d-flex pt-15 justify-content-center gap-3">
                    <Button
                      theme="light"
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <FormattedMessage id="form.cancel" />
                    </Button>

                    <Button
                      type="submit"
                      theme="primary"
                      isLoading={isSubmitting}
                    >
                      <FormattedMessage id={isGenerate ? 'queue_number.button.generate' : 'form.save'} />
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Col>

          {isEnableAppointment && (
            <Col xs={6}>
              <AppointmentTimelineCard
                title={intl.formatMessage({ id: 'appointment.agenda_patient_title' })}
                appointments={appointments ?? []}
              />
            </Col>
          )}
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export type { QueueNumberFormShape }

export { QueueNumberFormModal }
